import httpClient from "../../../../shared/api/httpClient";
import {
  downloadBlob,
  getFilenameFromContentDisposition,
} from "../../../../shared/utils/downloadBlob";

function getActiveLicenseId() {
  try {
    return localStorage.getItem("sabi_empresa_id") || "";
  } catch {
    return "";
  }
}

function buildQuery(params = {}) {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.append(key, value);
    }
  });

  const value = query.toString();
  return value ? `?${value}` : "";
}

function mapPeriod(raw = {}) {
  const status = String(raw.status || "").toLowerCase();

  return {
    id: raw.id,
    empresaId: raw.empresaId ?? raw.empresa_id ?? raw.licenseId ?? raw.license_id,
    periodStart: raw.dateFrom ?? raw.date_from ?? raw.periodStart ?? raw.period_start ?? raw.start_date,
    periodEnd: raw.dateTo ?? raw.date_to ?? raw.periodEnd ?? raw.period_end ?? raw.end_date,
    generatedAt: raw.generatedAt ?? raw.generated_at ?? raw.createdAt ?? raw.created_at,
    totalIncome: Number(raw.totalIncome ?? raw.total_income ?? raw.income_total ?? 0),
    totalExpense: Number(raw.totalExpense ?? raw.total_expense ?? raw.expense_total ?? 0),
    balance: Number(raw.balance ?? raw.saldo ?? 0),
    status:
      status === "draft"
        ? "DRAFT"
        : status === "cancelled"
        ? "CANCELLED"
        : "COMPLETED",
    name: raw.generatedName ?? raw.generated_name ?? raw.name ?? raw.nombre ?? "",
    deletedAt: raw.deletedAt ?? raw.deleted_at ?? null,
  };
}

function normalizeRangeStatus(status) {
  const value = String(status || "").toLowerCase();
  if (value === "draft") return "DRAFT";
  if (value === "cancelled") return "CANCELLED";
  return "COMPLETED";
}

function mapOccupiedRange(raw = {}) {
  return {
    id: raw.id,
    periodStart: raw.dateFrom ?? raw.date_from ?? raw.periodStart ?? raw.period_start ?? raw.start_date,
    periodEnd: raw.dateTo ?? raw.date_to ?? raw.periodEnd ?? raw.period_end ?? raw.end_date,
    status: normalizeRangeStatus(raw.status),
  };
}

function normalizeMovementType(value) {
  const type = String(value || "").toLowerCase();
  if (type === "expense" || type === "egreso") return "expense";
  if (type === "transfer" || type === "transferencia") return "transfer";
  return "income";
}

function normalizeMovementStatus(value) {
  const status = String(value || "").toLowerCase();
  if (status === "pending" || status === "pendiente") return "pending";
  if (status === "cancelled" || status === "canceled" || status === "cancelado") return "cancelled";
  return "confirmed";
}

function normalizeCategoryType(value) {
  const type = String(value || "").toLowerCase();
  if (type === "expense" || type === "egreso") return "expense";
  if (type === "both" || type === "ambos") return "both";
  return "income";
}

function mapSettings(raw = {}) {
  return {
    defaultMovementType: normalizeMovementType(
      raw.defaultMovementType ?? raw.default_movement_type
    ),
  };
}

function mapCategory(raw = {}) {
  return {
    id: raw.id,
    empresaId: raw.empresaId ?? raw.empresa_id ?? raw.licenseId ?? raw.license_id,
    name: raw.name ?? raw.nombre ?? "",
    type: normalizeCategoryType(
      raw.movementTypeScope ?? raw.movement_type_scope ?? raw.type ?? raw.tipo
    ),
    isActive: raw.isActive ?? raw.is_active ?? raw.active ?? true,
    deletedAt: raw.deletedAt ?? raw.deleted_at ?? null,
  };
}

function mapAccount(raw = {}) {
  return {
    id: raw.id,
    empresaId: raw.empresaId ?? raw.empresa_id ?? raw.licenseId ?? raw.license_id,
    name: raw.name ?? raw.nombre ?? "",
    type: raw.accountType ?? raw.account_type ?? raw.type ?? raw.tipo ?? "",
    currency: raw.currency ?? raw.moneda ?? "ARS",
    isActive: raw.isActive ?? raw.is_active ?? raw.active ?? true,
    deletedAt: raw.deletedAt ?? raw.deleted_at ?? null,
  };
}

function mapMovement(raw = {}) {
  const categoryId = raw.categoryId ?? raw.category_id ?? "";
  return {
    id: raw.id,
    periodId: raw.periodId ?? raw.period_id,
    clientId: raw.clientId ?? raw.client_id ?? null,
    date: raw.movementDate ?? raw.movement_date ?? raw.date ?? raw.fecha ?? "",
    movementType: normalizeMovementType(
      raw.movementType ?? raw.movement_type ?? raw.type ?? raw.tipo
    ),
    categoryId,
    categoryMissing: categoryId !== "" && categoryId !== null && raw.category === null,
    concept: raw.concept ?? raw.concepto ?? raw.description ?? raw.descripcion ?? "",
    amount: Number(raw.amount ?? raw.monto ?? 0),
    accountId: raw.accountId ?? raw.account_id ?? "",
    status: normalizeMovementStatus(raw.status ?? raw.estado),
    comment: raw.comment ?? raw.comentario ?? "",
    transferId: raw.transferId ?? raw.transfer_id ?? null,
    clientTransferId: raw.clientTransferId ?? raw.client_transfer_id ?? null,
    transferDirection: raw.transferDirection ?? raw.transfer_direction ?? null,
  };
}

function mapBalance(raw = {}) {
  const account = raw.account || raw.cuenta || {};
  const accountId =
    raw.accountId ??
    raw.account_id ??
    raw.treasuryAccountId ??
    raw.treasury_account_id ??
    account.id;
  const accountName =
    raw.accountName ??
    raw.account_name ??
    raw.cuentaNombre ??
    raw.cuenta_nombre ??
    account.name ??
    account.nombre ??
    raw.name ??
    raw.nombre ??
    "";

  const toNumber = (value, fallback = 0) =>
    value === null || value === undefined || value === "" ? fallback : Number(value);
  const toNullableNumber = (value) =>
    value === null || value === undefined || value === "" ? null : Number(value);

  const income = toNumber(raw.income ?? raw.totalIncome ?? raw.total_income ?? raw.ingresos);
  const expense = toNumber(raw.expense ?? raw.totalExpense ?? raw.total_expense ?? raw.egresos);
  const balance = toNumber(raw.balance ?? raw.saldo);
  const cashCount = toNullableNumber(
    raw.cashCount ??
    raw.cashCountAmount ??
    raw.cash_count ??
    raw.cash_count_amount ??
    raw.arqueo ??
    raw.arqueoCaja ??
    raw.arqueo_caja ??
    raw.arqueo_de_caja
  );
  const difference = toNullableNumber(
    raw.difference ??
    raw.cashDifference ??
    raw.cash_difference ??
    raw.diferencia
  );

  return {
    id: raw.id ?? accountId,
    accountId,
    accountName,
    income,
    expense,
    balance,
    cashCount,
    difference,
  };
}

function unwrapGenericList(response, mapper) {
  const payload = response && typeof response === "object" && "data" in response
    ? response.data
    : response;

  if (Array.isArray(payload)) return payload.map(mapper);
  if (Array.isArray(payload?.data)) return payload.data.map(mapper);
  return [];
}

function mapMovementBatchResponse(response) {
  const nestedPayload =
    response?.data &&
    !Array.isArray(response.data) &&
    (Array.isArray(response.data.data) || Array.isArray(response.data.movements))
      ? response.data
      : response;
  const rawMovements = Array.isArray(nestedPayload)
    ? nestedPayload
    : nestedPayload?.movements || nestedPayload?.data || [];
  const rawDerivedData =
    nestedPayload?.derivedData ||
    nestedPayload?.totals ||
    nestedPayload?.meta?.totals ||
    nestedPayload?.period ||
    nestedPayload?.meta?.period;
  const totalIncome =
    rawDerivedData?.totalIncome ??
    rawDerivedData?.total_income ??
    rawDerivedData?.incomeTotal ??
    rawDerivedData?.income_total;
  const totalExpense =
    rawDerivedData?.totalExpense ??
    rawDerivedData?.total_expense ??
    rawDerivedData?.expenseTotal ??
    rawDerivedData?.expense_total;
  const balance = rawDerivedData?.balance ?? rawDerivedData?.saldo;
  const hasDerivedData =
    totalIncome !== undefined || totalExpense !== undefined || balance !== undefined;

  return {
    movements: Array.isArray(rawMovements) ? rawMovements.map(mapMovement) : [],
    derivedData: hasDerivedData
      ? {
          ...(totalIncome !== undefined ? { totalIncome: Number(totalIncome) } : {}),
          ...(totalExpense !== undefined ? { totalExpense: Number(totalExpense) } : {}),
          ...(balance !== undefined ? { balance: Number(balance) } : {}),
        }
      : null,
  };
}

function unwrapList(response) {
  const payload = response && typeof response === "object" && "data" in response
    ? response.data
    : response;

  if (Array.isArray(payload)) return payload.map(mapPeriod);
  if (Array.isArray(payload?.data)) return payload.data.map(mapPeriod);
  return [];
}

export async function listTreasuryPeriods({ status, dateFrom, dateTo, search } = {}) {
  let mappedStatus = status;
  if (String(status).toLowerCase() === "completed") mappedStatus = "CLOSED";
  if (String(status).toLowerCase() === "closed") mappedStatus = "CLOSED";
  if (String(status).toLowerCase() === "draft") mappedStatus = "DRAFT";

  const query = buildQuery({
    status: mappedStatus,
    date_from: dateFrom,
    date_to: dateTo,
    search,
  });

  return unwrapList(await httpClient.get(`/treasury/periods${query}`));
}

export async function getTreasuryPeriod(periodId) {
  const response = await httpClient.get(`/treasury/periods/${periodId}`);
  return mapPeriod(response?.data || response);
}

export async function getTreasuryPeriodReport(periodId) {
  const response = await httpClient.get(`/treasury/periods/${periodId}/report`);
  const raw = response?.data || response || {};
  const summary = raw.summary || {};
  const mapCategorySummary = (category = {}) => ({
    categoryId: category.categoryId ?? category.category_id ?? null,
    categoryName: category.categoryName ?? category.category_name ?? "Sin categoría",
    movementCount: Number(category.movementCount ?? category.movement_count ?? 0),
    amount: Number(category.amount ?? category.total ?? 0),
    percentage: Number(category.percentage ?? category.participation ?? 0),
  });

  return {
    period: raw.period || {},
    summary: {
      totalIncome: Number(summary.totalIncome ?? summary.total_income ?? 0),
      totalExpense: Number(summary.totalExpense ?? summary.total_expense ?? 0),
      incomeCategories: (summary.incomeCategories || summary.income_categories || []).map(
        mapCategorySummary
      ),
      expenseCategories: (summary.expenseCategories || summary.expense_categories || []).map(
        mapCategorySummary
      ),
    },
    transfers: (raw.transfers || []).map((transfer = {}) => ({
      transferId: transfer.transferId ?? transfer.transfer_id,
      movementDate: transfer.movementDate ?? transfer.movement_date ?? "",
      sourceAccountName:
        transfer.sourceAccountName ?? transfer.source_account_name ?? "Cuenta de origen",
      destinationAccountName:
        transfer.destinationAccountName ??
        transfer.destination_account_name ??
        "Cuenta de destino",
      amount: Number(transfer.amount ?? 0),
    })),
  };
}

export async function listTreasuryOccupiedRanges() {
  const response = await httpClient.get("/treasury/periods/occupied-ranges");
  const payload = response?.data || response;
  const list = Array.isArray(payload) ? payload : payload?.data || [];
  return list.map(mapOccupiedRange);
}

export async function getTreasurySettings() {
  const licenseId = getActiveLicenseId();
  const response = await httpClient.get(
    `/admin/licenses/${licenseId}/treasury/settings`
  );
  return mapSettings(response?.data || response);
}

export async function listTreasuryMovementCategories({ isActive = true } = {}) {
  const query = buildQuery({
    is_active: isActive ? "true" : "",
    include_deleted: isActive ? "" : "true",
  });
  return unwrapGenericList(
    await httpClient.get(`/treasury/movement-categories${query}`),
    mapCategory
  );
}

export async function listTreasuryAccounts({ includeHistorical = true } = {}) {
  const endpoint = includeHistorical ? "/treasury/accounts/history" : "/treasury/accounts";
  return unwrapGenericList(await httpClient.get(endpoint), mapAccount);
}

export async function listTreasuryPeriodBalances(periodId) {
  return unwrapGenericList(
    await httpClient.get(`/treasury/periods/${periodId}/balances`),
    mapBalance
  );
}

export async function saveTreasuryPeriodCashCounts(periodId, balances) {
  const results = await Promise.all(
    balances.map(async (balance, index) => {
      try {
        const response = await httpClient.patch(
          `/treasury/periods/${periodId}/balances/accounts/${balance.accountId}/cash-count`,
          { cashCountAmount: balance.cashCount }
        );
        return { ok: true, data: mapBalance(response?.data || response) };
      } catch (error) {
        return {
          ok: false,
          error,
          row: {
            id: balance.id,
            accountId: balance.accountId,
            index,
            field: "cashCount",
            message:
              error?.data?.message ||
              error?.response?.data?.message ||
              error?.message ||
              "No se pudo guardar el arqueo.",
          },
        };
      }
    })
  );

  const saved = results.filter((result) => result.ok).map((result) => result.data);
  const failed = results.filter((result) => !result.ok);

  if (failed.length) {
    const error = new Error("No se pudieron guardar todos los arqueos.");
    error.status = failed[0].error?.status || failed[0].error?.response?.status;
    error.data = {
      rows: failed.map((result) => result.row),
      saved,
    };
    throw error;
  }

  return saved;
}

export async function createTreasuryPeriod({ periodStart, periodEnd } = {}) {
  const response = await httpClient.post("/treasury/periods", {
    dateFrom: periodStart,
    dateTo: periodEnd,
  });

  return mapPeriod(response?.data || response);
}

export async function deleteTreasuryPeriod(periodId) {
  return httpClient.delete(`/treasury/periods/${periodId}`);
}

export async function completeTreasuryPeriod(periodId) {
  const response = await httpClient.post(`/treasury/periods/${periodId}/complete`);
  return mapPeriod(response?.data || response);
}

export async function downloadTreasuryPeriodReportPdf(period) {
  const periodId = period?.id;
  const periodStart = period?.periodStart || period?.dateFrom;
  const periodEnd = period?.periodEnd || period?.dateTo;
  const response = await httpClient.getBlobWithHeaders(
    `/treasury/periods/${periodId}/report/pdf`,
    {
      headers: {
        Accept: "application/pdf",
      },
    }
  );
  const headerFileName = getFilenameFromContentDisposition(
    response.headers?.get?.("content-disposition")
  );
  const fileName =
    headerFileName || `cierre-tesoreria-${periodStart}-a-${periodEnd}.pdf`;
  const blob = response.data;
  downloadBlob(blob, fileName);
  return { ok: true, fileName };
}

export async function listTreasuryMovements(periodId) {
  return unwrapGenericList(
    await httpClient.get(`/treasury/periods/${periodId}/movements`),
    mapMovement
  );
}

export async function saveTreasuryMovementsBatch(periodId, movements) {
  const formattedMovements = movements.map((m) => {
    const type = String(m.movementType || "").toLowerCase();
    const status = String(m.status || "").toLowerCase();
    const isTransfer = type === "transfer";

    return {
      ...(m.id ? { id: Number(m.id) } : {}),
      ...(m.clientId ? { clientId: String(m.clientId) } : {}),
      ...(m.transferId ? { transferId: String(m.transferId) } : {}),
      ...(m.clientTransferId ? { clientTransferId: String(m.clientTransferId) } : {}),
      ...(m.transferDirection ? { transferDirection: String(m.transferDirection).toUpperCase() } : {}),
      movementDate: m.movementDate || m.date,
      movementType: isTransfer ? "TRANSFER" : type === "expense" ? "EXPENSE" : "INCOME",
      categoryId: isTransfer ? null : m.categoryId ? Number(m.categoryId) : null,
      concept: String(m.concept || "").trim(),
      amount: Number(m.amount ?? m.amountInput ?? 0),
      accountId: Number(m.accountId),
      status: status === "pending" ? "PENDING" : status === "cancelled" ? "CANCELLED" : "CONFIRMED",
      comment: m.comment || null,
    };
  });

  return mapMovementBatchResponse(
    await httpClient.post(`/treasury/periods/${periodId}/movements/batch`, { movements: formattedMovements })
  );
}

export async function deleteTreasuryMovement(periodId, movementId) {
  return httpClient.delete(`/treasury/periods/${periodId}/movements/${movementId}`);
}
